import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import '../styles.css';
import 'react-datepicker/dist/react-datepicker.css';
import ReactDatePicker from 'react-datepicker';
import { format, parseISO } from 'date-fns';
import { useUsers } from '../contexts/UsersContext';
import { useHabits } from '../contexts/HabitsContext';
import { useHabitObservations } from '../contexts/HabitObservationsContext';

export default function Habits({ apiUrl, isAdmin }) {
  const { habitObservations, setHabitObservations } = useHabitObservations();
  const { habits, setHabits } = useHabits();
  const { users, setUsers } = useUsers();

  const [
    newHabitObservationDateCompleted,
    setNewHabitObservationDateCompleted,
  ] = useState(new Date());

  const [ selectedUserId, setSelectedUserId ] = useState(null);

  useEffect(() => {
    if (users.length > 0 && selectedUserId === null) {
      setSelectedUserId(users[0].id);
    }
  }, [users, selectedUserId]);

  useEffect(() => {
    fetchHabitObservations();
  }, [isAdmin]);

  const fetchHabitObservations = () => {
    const fullHabitObservationsUrl = `${apiUrl}/habit_observations`;
    axios
      .get(fullHabitObservationsUrl, { withCredentials: true })
      .then((response) => {
        console.log('Received observations list:', response.data);
        setHabitObservations(response.data);
      })
      .catch((error) => {
        console.log('Error fetching observations:', error);
        setHabitObservations([]);
      });
  };

  const handleCreateHabitObservation = (habitId, completed, dateCompleted) => {
    const createUrl = `${apiUrl}/habit_observations`;
    let points = 0;
    if (completed === true) {
      points = parseInt(prompt('How many points were earned?', '1'));
      if (!points) return;
    }

    const newHabitObservation = {
      habit_id: habitId,
      completed,
      date_completed: format(dateCompleted, 'yyyy-MM-dd'),
      points,
    };

    axios
      .post(createUrl, newHabitObservation, { withCredentials: true })
      .then((response) => {
        console.log(
          `Habit Observation for '${habitId}' created successfully.`,
          response.data
        );
        setHabitObservations((prevHabitObservations) => [
          ...prevHabitObservations,
          response.data,
        ]);
        console.log('habit observations after: ', habitObservations);
      })
      .catch((error) => {
        console.error(
          `Error creating observation for habit '${habitId}:`,
          error
        );
        alert('Failed to create the habit. Please try again.');
      });
  };

  const handleDeleteHabitObservation = (habitObservationId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this item?'
    );
    if (!confirmDelete) {
      return;
    }

    const deleteUrl = `${apiUrl}/habit_observations/${habitObservationId}`;
    axios
      .delete(deleteUrl, { withCredentials: true })
      .then(() => {
        console.log(
          `Habit Observation with ID ${habitObservationId} deleted successfully.`
        );
        setHabitObservations(
          habitObservations.filter(
            (habitObservation) => habitObservation.id !== habitObservationId
          )
        );
      })
      .catch((error) => {
        console.error(
          `Error deleting habit observation with ID ${habitObservationId}:`,
          error
        );
        alert('Failed to delete the item. Please try again.');
      });
  };

  const findUserById = (userId) => {
    return users.find((u) => u.id === userId);
  };

  const findHabitById = (habitId) => {
    return habits.find((h) => h.id === habitId);
  };

  const normalizeDate = (date) => format(parseISO(date), 'yyyy-MM-dd');

  const filteredObservations = useMemo(() => {
    return habits
      .filter(h => (h.user_id || '') == selectedUserId)
      .map((habit) => {
      const existingObservation = habitObservations.find(
        (observation) =>
          observation.habit_id === habit.id &&
          normalizeDate(observation.date_completed) ===
            format(newHabitObservationDateCompleted, 'yyyy-MM-dd')
      );
      return existingObservation || { habit_id: habit.id, completed: null, points: habit.point_description };
    });
  }, [habits, habitObservations, newHabitObservationDateCompleted, selectedUserId]);

  return isAdmin ? (
    <div className="habit-observations-container">
      <div className="date-picker-container">
        <label htmlFor="datePicker">Select Date: </label>
        <ReactDatePicker
          id="datePicker"
          selected={newHabitObservationDateCompleted}
          onChange={(date) => setNewHabitObservationDateCompleted(date)}
          dateFormat="yyyy-MM-dd"
          className="custom-date-picker"
        />
      </div>
      <div className="user-picker-container">
        <label htmlFor="userPicker">Select User:</label>
        <select id="userPicker" value={selectedUserId || ''} onChange={(e) => setSelectedUserId(e.target.value)}>
          {users.sort((a, b) => a.name.localeCompare(b.name)).map(user => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>
      </div>
      <div id="habitObservationsTable">
        <table className="custom-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Habit</th>
              <th>Points</th>
              <th>Completed?</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredObservations
              .filter(obs => !(obs.completed === null && findHabitById(obs.habit_id).is_hidden)) // if it's hidden and empty, don't show it
              .map(
              (habitObservation, index) => {
                const habit = findHabitById(habitObservation.habit_id);
                const user = habit ? findUserById(habit.user_id) : null;
                return (
                  <tr key={habitObservation.id || `temp-${index}`}>
                    <td>{habitObservation.id || ''}</td>
                    <td>{habit ? habit.description : 'Unknown Habit'}</td>
                    <td>{habitObservation.points || ''}</td>
                    <td>
                      {habitObservation.completed === null
                        ? ''
                        : habitObservation.completed === true
                        ? 'Yes'
                        : 'No'}
                    </td>
                    <td>
                      {habitObservation.id ? (
                        <button
                          onClick={() =>
                            handleDeleteHabitObservation(habitObservation.id)
                          }
                        >
                          Delete
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() =>
                              handleCreateHabitObservation(
                                habit.id,
                                true,
                                newHabitObservationDateCompleted
                              )
                            }
                          >
                            Yes
                          </button>
                          <button
                            onClick={() =>
                              handleCreateHabitObservation(
                                habit.id,
                                false,
                                newHabitObservationDateCompleted
                              )
                            }
                          >
                            No
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    ''
  );
}
